import { HttpClient } from "@angular/common/http";
import { EnvService } from "./env.service";
import { Injectable } from "@angular/core";
import { NotificationService } from "@apps/manage-app/src/app/shared/services/notification.service";
import { map, Observable } from "rxjs";

export enum GRAPHQL_OPERATION_NAMES {
  APPOINTMENTS_MULTISITE_PRACTICE = "appointmentsMultisitePractice",
  APPOINTMENTS_MULTISITE_SITE = "appointmentsMultisiteSite",
  COMMON = "common",
  DELETE_DEVICE = "deleteDevice",
  DEVICES = "devices",
  LINK_DEVICE = "linkDevice",
  PING_DEVICE = "pingDevice",
  PLANS = "plans",
  PLAN_CATEGORIES = "planCategories",
  PRACTICE = "PRACTICE",
  SITE_SETTINGS = "siteSettings",
  STATISTICS = "statistics",
  UNLOCK_DEVICE = "unlockDevice",
  UPDATE_APPOINTMENT_SETTINGS = "updateAppointmentSettings",
  UPDATE_DEVICE = "updateDevice",
  UPDATE_FEATURES = "updateFeatures",
  UPDATE_PRACTICE_SETTINGS = "updatePracticeSettings",
  UPDATE_SITE_PAYMENT_PLAN_CATEGORY = "updateSitePaymentPlanCategory",
  ELEVATE_TO_L5 = "elevateToL5",
  WRITE_LOG = "writeLog",
  DEV_LINKS = "devLinks",
  UPDATE_SITE_APPOINTMENT_TYPE = "updateSiteAppointmentType",
  APPOINTMENT_TYPE_CATEGORY = "appointmentTypeCategory",
  DELETE_APPOINTMENT_TYPE = "deleteAppointmentType",
  CREATE_APPOINTMENT_TYPE = "createAppointmentType",
  CREATE_SITE_APPOINTMENT_TYPE = "createSiteAppointmentType",
  UPDATE_APPOINTMENT_TYPE = "updateAppointmentType",
  GET_APPOINTMENTS_MULTISITE_SITE = "getAppointmentsMultisiteSite",
  APPOINTMENT_TYPE_DESCRIPTIONS = "appointmentTypeDescriptions",
  AVAILABILITY_GUIDE = "availabilityGuide",
  CREATE_BRAND = "createBrand",
  DELETE_BRAND = "deleteBrand",
  UPDATE_BRAND = "updateBrand",
  UPDATE_SITE_ALIASES = "updateSiteAliases",
  UPDATE_SITE_BRAND = "updateSiteBrand",
  GET_PORTAL_BRANDS = "getPortalBrands",
  GET_PRACTICE_BRANDS = "getPracticeBrands",
  CHECK_SUBDOMAIN = "checkSubdomain",
  ADDITIONAL_SITE_DATA = "additionalSiteData",
  QUEUE_REFRESH_CACHE = "queueRefreshCache",
  DEV = "dev",
  QUEUE_BULK_CREATE = "queueBulkCreate",
  QUEUE_BULK_DELETE = "queueBulkDelete",
  UPDATE_SITE_PAYMENT_PLAN = "updateSitePaymentPlan",
  PORTAL_VIEWER = "portalViewer",
  IMPERSONATE_PATIENT = "impersonatePatient",
  PATIENT_SEARCH = "patientSearch",
  UNLOCK_PATIENT_ACCOUNT = "unlockPatientAccount",
  SEND_TASK_REMINDER_NOTIFICATION = "sendTaskReminderNotification",
  UPDATE_PRACTITIONER = "updatePractitioner",
  GET_PRACTITIONERS = "getPractitioners",
  SUPPORT_QUERY_LIST = "supportQueryList",
  SUPPORT_QUERY_RESULT = "supportQueryResult",
  CLEAR_API_CACHE = "clearApiCache",
}

@Injectable({
  providedIn: "root",
})
export class HttpService {
  constructor(private _http: HttpClient, private _envService: EnvService, private _notificationService: NotificationService) {}

  public async post_rest<T>(endpoint: string, body?: any, error_action: string | null = null, showReload = true): Promise<T> {
    try {
      const url = `${this._envService.env.REST_URL}/api${endpoint}`;
      const p = this._http.post<T>(url, body).pipe().toPromise();
      const response: any = await p;
      if (response.errors) throw new Error("loading issue");
      return response.data;
    } catch (err) {
      if (error_action) this._notificationService.reportWarning(`Failed to ${error_action}`, err, showReload);
      throw err;
    }
  }

  private _graphRequest<T>(operationName: GRAPHQL_OPERATION_NAMES, query: string, options: any = {}): Observable<T> {
    options.observe = "response";
    options.body = {
      query,
      operationName,
    };
    options.params = options.params || {};
    options.params.operationName = operationName;

    return this._http.post<T>(this._graphUrl, { query }, options).pipe(
      map((res: any) => {
        if (res && res.ok === true) {
          return res.body?.data;
        }

        return res;
      })
    );
  }

  public query<T>(operationName: GRAPHQL_OPERATION_NAMES, query: string): Observable<T> {
    const namedQuery = `query ${operationName} ${query}`;
    return this._graphRequest(operationName, namedQuery);
  }

  public mutation<T>(operationName: GRAPHQL_OPERATION_NAMES, query: string): Observable<T> {
    const namedQuery = `mutation ${operationName} ${query}`;
    return this._graphRequest(operationName, namedQuery);
  }

  public async fetchDataQuery<T>(
    operationName: GRAPHQL_OPERATION_NAMES,
    queryString: string,
    error_action: string | null = null,
    showReload = true
  ): Promise<T> {
    const fn = () => this.query<T>(operationName, queryString);
    return this._fetchData(fn, error_action, showReload);
  }

  public async fetchDataMutation<T>(
    operationName: GRAPHQL_OPERATION_NAMES,
    queryString: string,
    error_action: string | null = null,
    showReload = true
  ): Promise<T> {
    const fn = () => this.mutation<T>(operationName, queryString);
    return this._fetchData(fn, error_action, showReload);
  }

  private async _fetchData<T>(fn: () => Observable<T>, error_action: string | null = null, showReload = true): Promise<T> {
    try {
      const response: any = await fn().pipe().toPromise();

      if (response.errors) throw new Error(response.errors[0].message);

      return response;
    } catch (err) {
      console.error("error fetching data from graph", err);
      if (error_action) this._notificationService.reportWarning(`Failed to ${error_action}`, err, showReload);
      throw err;
    }
  }

  public post(url: string, body: any): Observable<any> {
    url = this._envService.env.REST_LEGACY_URL + url;
    return this._http.post(url, body);
  }

  private get _graphUrl(): string {
    return this._envService.env.GRAPH_URL;
  }
}
